import { Formik, Form } from "formik";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { Icon } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { exportSKUPackTypeWeight, uploadSKUPackTypeWeight } from "services/data.internal.service";

import MDTypography from "components/MDTypography";
import { useState } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import { StripedDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { Error_, LoadingDialog, ExportDialog, InfoAlert } from "ui-components/Alert";
import { downloadFile, getFileName } from "layouts/views/common/file";

function UploadSKUPackTypeWeight() {
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [uploadResps, setUploadResps] = useState([]);

  const exportMutation = useMutation(exportSKUPackTypeWeight, {
    onSuccess: (response) => {
      downloadFile(response.data, `${getFileName("SKUPackTypeInfo", customerCode)}.xlsx`);
    },
    onError: (data) => setAlert(<Error_ data={data} onChange={setAlert} />),
  });

  const uploadMutation = useMutation(uploadSKUPackTypeWeight, {});
  const uploadStatus = uploadMutation.status === "loading";

  const handleSubmit = (values, actions) => {
    let formData = new FormData();
    formData.append("files", values.files[0], values.files[0].name);
    formData.append("customerCode", customerCode);

    uploadMutation.mutate(formData, {
      onSuccess: (data) => {
        document.getElementById("packtype-weight-files").value = null;
        actions.resetForm();
        setUploadResps(data.data.data);
      },
      onError: (data) => {
        setAlert(<Error_ data={data} onChange={setAlert} />);
      },
    });
    actions.setSubmitting(false);
  };

  return (
    <InboundLayout lg={12}>
      {alert}
      {exportMutation.status === "loading" && <ExportDialog />}
      {uploadStatus && <LoadingDialog />}
      <InboundHeaderCard>
        <InfoAlert content="Exports SKU Pack Types missing Case/Weight info (customer's active records where Case UPM, Gross Weight or Carton Weight is 0). Upload the corrected sheet back to only overwrite those 4 fields - nothing else changes. Case UPM is locked once it already has a value (yellow = still needs one) and can't be changed once set. Gross Weight and Carton Weight stay editable even when already set - you can correct them again any time. Cubic UPM is optional - leave it blank to keep it unchanged." />
        <MDBox>
          <MDBox pb={1}>
            <MDTypography color="info" variant="h3">
              Upload SKU Pack Type Weight
            </MDTypography>
            <MDTypography color="dark" variant="h3">
              Selected Customer: {customerCode}
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => exportMutation.mutate({ customerCode })}
          >
            <Icon>download</Icon>&nbsp;Export Missing Case/Weight
          </MDButton>

          <Formik initialValues={{ files: [] }} onSubmit={handleSubmit}>
            {({ values, setFieldValue }) => (
              <Form>
                <MDBox mt={2}>
                  <input
                    type="file"
                    name="files"
                    id="packtype-weight-files"
                    onChange={(e) => {
                      setUploadResps([]);
                      setFieldValue("files", e.target.files);
                    }}
                  />
                </MDBox>
                <MDBox mt={2} display="flex" justifyContent="center">
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={!values.files.length > 0 || uploadStatus}
                  >
                    {uploadStatus ? "Uploading" : "Upload"}
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
        </MDBox>
      </InboundHeaderCard>

      {uploadResps.length > 0 && (
        <InboundHeaderCard>
          <MDTypography sx={{ marginBottom: ".5rem" }} color="info" variant="h4">
            Upload Response
          </MDTypography>
          <StripedDataGrid
            columns={[
              {
                headerName: "Status",
                field: "success",
                align: "center",
                renderCell: (cell) =>
                  cell.row.success ? (
                    <StatusIcon color="success" icon="done" />
                  ) : (
                    <StatusIcon color="error" icon="close" />
                  ),
              },
              { headerName: "SKU Code", field: "skuCode", width: 200 },
              {
                headerName: "Message",
                field: "message",
                flex: 1,
                wordWrapEnabled: true,
                renderCell: (cell) => (
                  <div>
                    {cell.row.message.map((c, i) => (
                      <p key={i}>{c}</p>
                    ))}
                  </div>
                ),
              },
            ]}
            rows={uploadResps}
            getRowHeight={(cell) => {
              if (cell.model.message.length > 2) {
                return "auto";
              } else {
                return 50;
              }
            }}
          />
        </InboundHeaderCard>
      )}
    </InboundLayout>
  );
}

const StatusIcon = ({ color, icon }) => (
  <MDButton color={color} variant="outlined" size="small" iconOnly circular>
    <Icon sx={{ fontWeight: "bold" }}>{icon}</Icon>
  </MDButton>
);

export default UploadSKUPackTypeWeight;
